import { BadgeCheck, Building2, DoorOpen, LayoutDashboard, PackageOpen, ScanLine, UserCog, Users } from "lucide-react";
import type { JSX } from "react";
import { NavLink, Route, Routes } from "react-router-dom";

import { useActiveMembership } from "../common/membership/ActiveMembership.js";
import { AccountMenu } from "../common/membership/AccountMenu.js";
import { ROLE_LABELS } from "../common/membership/roles.js";
import { AdminTenantsPage } from "../admin/pages/AdminTenantsPage.js";
import { AdminUsersPage } from "../admin/pages/AdminUsersPage.js";
import { DashboardPage } from "../company/pages/DashboardPage.js";
import { ServicesPage } from "../company/pages/ServicesPage.js";
import { VenuesPage } from "../company/pages/VenuesPage.js";
import { MembersPage } from "../company/members/MembersPage.js";
import { TeamPage } from "../company/team/TeamPage.js";
import { ScanPage } from "../controller/pages/ScanPage.js";

/**
 * Die Firmen-Shell: Navigation links, Inhalt rechts, auf dem Telefon untereinander.
 *
 * Welche Einträge erscheinen, entscheidet das Recht — nicht die Rolle. Eine Rolle kann
 * sich ändern oder dazukommen; das Recht an einer Aktion bleibt. Verborgen ist dabei
 * nur die Abkürzung: durchgesetzt wird jedes dieser Rechte auf dem Server.
 */
const NAV = [
  { to: "/", label: "Übersicht", Icon: LayoutDashboard, permission: null },
  { to: "/spielplan", label: "Angebote", Icon: PackageOpen, permission: "service.read" },
  { to: "/orte", label: "Orte", Icon: DoorOpen, permission: "venue.read" },
  { to: "/scan", label: "Einlass", Icon: ScanLine, permission: "booking.scan" },
  { to: "/mitglieder", label: "Mitglieder", Icon: BadgeCheck, permission: "member.manage" },
  { to: "/team", label: "Team", Icon: UserCog, permission: "team.read" },
  { to: "/admin/unternehmen", label: "Unternehmen", Icon: Building2, permission: "platform.manage" },
  { to: "/admin/konten", label: "Konten", Icon: Users, permission: "platform.user.manage" },
] as const;

export function CompanyShell(): JSX.Element {
  const { active, can } = useActiveMembership();
  const sichtbar = NAV.filter((entry) => entry.permission === null || can(entry.permission));

  return (
    <div className="role-company flex min-h-full flex-col bg-paper lg:flex-row">
      <aside className="flex flex-col border-b border-line bg-surface lg:w-64 lg:border-b-0 lg:border-r">
        <div className="flex items-center gap-2.5 px-4 pb-3 pt-safe-top lg:px-5 lg:pt-5">
          <span
            className="mt-3 grid h-9 w-9 flex-none place-items-center rounded-sm text-[13px] font-extrabold text-white lg:mt-0"
            style={{ background: "linear-gradient(145deg, var(--brand), var(--accent))" }}
            aria-hidden="true"
          >
            BK
          </span>
          <div className="mt-3 min-w-0 flex-1 lg:mt-0">
            <p className="micro">{active ? ROLE_LABELS[active.role].label : "Backoffice"}</p>
            <p className="truncate text-[14px] font-bold text-ink">
              {active?.tenantName ?? "BookKing"}
            </p>
          </div>
          {/* Auf schmalen Fenstern steht das Konto-Menü hier oben; auf breiten rückt
              es in die Kopfzeile des Inhalts. */}
          <span className="mt-3 lg:hidden">
            <AccountMenu />
          </span>
        </div>

        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:overflow-visible">
          {sichtbar.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                [
                  "flex min-h-[44px] items-center gap-2.5 whitespace-nowrap rounded px-3 text-[14px] font-bold",
                  isActive ? "bg-brand-soft text-role" : "text-ink-2 hover:text-ink",
                ].join(" ")
              }
            >
              <Icon size={18} aria-hidden="true" />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="hidden items-center gap-3 border-b border-line bg-surface px-5 py-2.5 lg:flex">
          <span className="flex-1" />
          <AccountMenu />
        </header>

        {/* Der Inhaltsbereich ist selbst das Panel — keine Kachel mit Rahmen darin. */}
        <main className="min-w-0 flex-1 pb-safe-bottom">
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/spielplan" element={<ServicesPage />} />
            <Route path="/orte" element={<VenuesPage />} />
            <Route path="/scan" element={<ScanPage />} />
            <Route path="/mitglieder" element={<MembersPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/admin/unternehmen" element={<AdminTenantsPage />} />
            <Route path="/admin/konten" element={<AdminUsersPage />} />
            <Route path="*" element={<DashboardPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
